import { type MappingPreviewDto, Role, type RoleMapping, RoleOutcome, type RoleRule, TransformType } from '@today/types'
import { SelectField } from '@today/ui-components'
import { Button, cn, Input, Switch } from '@today/ui-primitives'
import { AttributeInput } from './attribute-input.view'
import { MAPPABLE_ROLE_OPTIONS } from './attribute-mapping-labels'
import { TransformEditor } from './transform-editor.view'

/**
 * Props for {@link RoleMappingTable}.
 *
 * @property roles - The draft's role mapping; `null` = role sync off.
 * @property attributeNames - The sample's attribute names, offered by the combobox.
 * @property preview - The server preview's role result, when there is one.
 * @property onChange - Reports the new role mapping, or `null` when switched off.
 * @property error - A refusal located on the role mapping (`422`), shown under the table.
 * @property isDisabled - Disables every control.
 */
export interface RoleMappingTableProps {
	roles: RoleMapping | null
	attributeNames: string[]
	preview?: MappingPreviewDto['role'] | null
	onChange: (roles: RoleMapping | null) => void
	error?: string | null
	isDisabled?: boolean
}

const STARTER: RoleMapping = {
	idpAttribute: '',
	transform: { type: TransformType.NONE },
	rules: [],
	defaultRole: Role.ORG_USER,
}

/** `rules` with the entry at `from` moved to `to`. */
const moved = (rules: RoleRule[], from: number, to: number): RoleRule[] => {
	const next = [...rules]
	const [rule] = next.splice(from, 1)
	next.splice(to, 0, rule)
	return next
}

type RulesProps = {
	roles: RoleMapping
	matchedIndex: number | null
	onChange: (roles: RoleMapping) => void
	isDisabled: boolean
}

type RuleRowProps = {
	rule: RoleRule
	index: number
	count: number
	isMatched: boolean
	isDisabled: boolean
	onUpdate: (patch: Partial<RoleRule>) => void
	onMove: (to: number) => void
	onDelete: () => void
}

/** One rule: IdP value → role, with move up / down and delete. */
function RuleRow({ rule, index, count, isMatched, isDisabled, onUpdate, onMove, onDelete }: RuleRowProps) {
	const n = index + 1
	return (
		<li
			aria-label={`Rule ${n}`}
			className={cn(
				'flex flex-wrap items-end gap-2 rounded-ds-md border border-ds-border p-2',
				isMatched && 'border-ds-primary bg-ds-surface-muted',
			)}
		>
			<Input
				size="sm"
				aria-label={`IdP value for rule ${n}`}
				value={rule.idpValue}
				disabled={isDisabled}
				onChange={(event) => onUpdate({ idpValue: event.target.value })}
			/>
			<SelectField
				label={`TODAY role for rule ${n}`}
				options={MAPPABLE_ROLE_OPTIONS}
				value={rule.role}
				onValueChange={(role) => onUpdate({ role: role as Role })}
			/>
			<Button size="sm" variant="muted" isDisabled={isDisabled || index === 0} onPress={() => onMove(index - 1)}>
				Move up
			</Button>
			<Button
				size="sm"
				variant="muted"
				isDisabled={isDisabled || index === count - 1}
				onPress={() => onMove(index + 1)}
			>
				Move down
			</Button>
			<Button size="sm" variant="muted" isDisabled={isDisabled} onPress={onDelete}>
				Delete
			</Button>
			{isMatched ? <span className="text-ds-sm font-medium text-ds-text">Rule {n} matches</span> : null}
		</li>
	)
}

/** The ordered rules. */
function RuleList({ roles, matchedIndex, onChange, isDisabled }: RulesProps) {
	const setRules = (rules: RoleRule[]) => onChange({ ...roles, rules })
	return (
		<ol className="flex flex-col gap-2">
			{roles.rules.map((rule, index) => (
				<RuleRow
					// biome-ignore lint/suspicious/noArrayIndexKey: rules have no id — the position is the identity, and every input is controlled, so a reorder re-renders correct values.
					key={index}
					rule={rule}
					index={index}
					count={roles.rules.length}
					isMatched={matchedIndex === index}
					isDisabled={isDisabled}
					onUpdate={(patch) => setRules(roles.rules.map((old, i) => (i === index ? { ...old, ...patch } : old)))}
					onMove={(to) => setRules(moved(roles.rules, index, to))}
					onDelete={() => setRules(roles.rules.filter((_, i) => i !== index))}
				/>
			))}
		</ol>
	)
}

/** The switched-on body: attribute, transform, rules, default role. */
function RoleMappingBody({
	roles,
	attributeNames,
	preview,
	onChange,
	isDisabled = false,
}: Omit<RoleMappingTableProps, 'roles' | 'onChange' | 'error'> & {
	roles: RoleMapping
	onChange: (roles: RoleMapping) => void
}) {
	const matchedIndex = preview?.outcome === RoleOutcome.RULE ? preview.ruleIndex : null
	return (
		<>
			<AttributeInput
				label="IdP attribute for the role"
				value={roles.idpAttribute}
				options={attributeNames}
				onChange={(idpAttribute) => onChange({ ...roles, idpAttribute })}
				isDisabled={isDisabled}
			/>
			<TransformEditor
				label="the role"
				value={roles.transform}
				isDisabled={isDisabled}
				onChange={(transform) => onChange({ ...roles, transform })}
			/>
			<p className="text-ds-sm text-ds-text-muted">First matching rule wins</p>
			<RuleList roles={roles} matchedIndex={matchedIndex} onChange={onChange} isDisabled={isDisabled} />
			<Button
				size="sm"
				variant="secondary"
				isDisabled={isDisabled}
				onPress={() => onChange({ ...roles, rules: [...roles.rules, { idpValue: '', role: Role.ORG_USER }] })}
			>
				Add rule
			</Button>
			<SelectField
				label="Default role"
				options={MAPPABLE_ROLE_OPTIONS}
				value={roles.defaultRole}
				onValueChange={(role) => onChange({ ...roles, defaultRole: role as Role })}
			/>
			{preview?.outcome === RoleOutcome.DEFAULT ? (
				<p className="text-ds-sm font-medium text-ds-text">Default role applies</p>
			) : null}
		</>
	)
}

/**
 * The role mapping table (REQ-205): an on/off switch, the role attribute and
 * transform, ordered rules to the two branch roles only, and the required
 * default role. The preview's matched rule is highlighted. Admin and platform
 * roles are never offered. Pure and controlled.
 */
export function RoleMappingTable({ roles, onChange, error, ...rest }: RoleMappingTableProps) {
	return (
		<section className="flex flex-col gap-3 rounded-ds-md border border-ds-border p-4" aria-label="Role mapping">
			<h3 className="text-ds-base font-semibold text-ds-text">Role</h3>
			<p role="note" className="text-ds-sm text-ds-text">
				Permissions are regenerated only when the mapped role changes.
			</p>
			<Switch isSelected={roles !== null} isDisabled={rest.isDisabled} onChange={(on) => onChange(on ? STARTER : null)}>
				Map roles from the IdP
			</Switch>
			{roles ? (
				<RoleMappingBody roles={roles} onChange={onChange} {...rest} />
			) : (
				<p className="text-ds-sm text-ds-text-muted">Role sync off</p>
			)}
			{error ? (
				<p role="alert" className="text-ds-sm text-ds-danger">
					{error}
				</p>
			) : null}
		</section>
	)
}
